/**
 * AEOOnboardingWizard — Service Path
 *
 * 4-step onboarding flow for service-based sites:
 *  Step 1: Booking system detection
 *  Step 2: Markets & languages
 *  Step 3: Contact methods
 *  Step 4: Trigger backend orchestrator (auto)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertCircle,
  Play,
  RefreshCw,
  CheckCircle,
  Loader2,
  Calendar,
  Globe,
  Phone,
  Mail,
  FileText,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  XCircle
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

// ── Types ──────────────────────────────────────────

type ServiceStep = 'booking_system' | 'markets_languages' | 'contact_methods' | 'run_onboarding';

interface ServiceWizardState {
  currentStep: ServiceStep;
  bookingSystem: string | null;
  markets: string[];
  languages: string[];
  contactMethods: string[];
}

interface OnboardingProgress {
  state: 'idle' | 'running' | 'completed' | 'failed';
  currentStep?: string;
  completedSteps: string[];
  totalSteps: number;
  error?: string;
}

const STEPS: { key: ServiceStep; label: string }[] = [
  { key: 'booking_system', label: 'Booking System' },
  { key: 'markets_languages', label: 'Markets & Languages' },
  { key: 'contact_methods', label: 'Contact Methods' },
  { key: 'run_onboarding', label: 'Run Onboarding' },
];

const BOOKING_SYSTEMS = [
  { value: 'calendly', label: 'Calendly', icon: Calendar },
  { value: 'guesty', label: 'Guesty', icon: Calendar },
  { value: 'tripadvisor', label: 'TripAdvisor / Viator', icon: Globe },
  { value: 'bookeo', label: 'Bookeo', icon: Calendar },
  { value: 'fareharbor', label: 'FareHarbor', icon: Calendar },
  { value: 'manual', label: 'Manual / Custom', icon: FileText },
  { value: 'none', label: 'No booking system', icon: XCircle },
];

const COMMON_MARKETS = [
  'Spain', 'United States', 'United Kingdom', 'Germany', 'France',
  'Italy', 'Netherlands', 'Portugal', 'Canada', 'Australia',
];

const COMMON_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'ca', label: 'Catalan' },
];

const CONTACT_METHODS = [
  { value: 'lead_form', label: 'Lead / Contact Form', icon: FileText },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: Phone },
];

// ── Persistence helpers ────────────────────────────

const STORAGE_KEY = 'aeo-service-wizard';

function loadState(siteId: string): ServiceWizardState | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}-${siteId}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveState(siteId: string, state: ServiceWizardState) {
  localStorage.setItem(`${STORAGE_KEY}-${siteId}`, JSON.stringify(state));
}

function clearState(siteId: string) {
  localStorage.removeItem(`${STORAGE_KEY}-${siteId}`);
}

// ── Component ──────────────────────────────────────

export function ServiceAEOOnboardingWizard() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id || '';

  const [wizardState, setWizardState] = useState<ServiceWizardState>(() =>
    loadState(siteId) || {
      currentStep: 'booking_system',
      bookingSystem: null,
      markets: [],
      languages: [],
      contactMethods: [],
    }
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgress>({
    state: 'idle',
    completedSteps: [],
    totalSteps: 5,
  });

  const pollRef = useRef<NodeJS.Timeout | null>(null);

  // Persist on change
  useEffect(() => {
    if (siteId) saveState(siteId, wizardState);
  }, [siteId, wizardState]);

  // Reset when site changes
  useEffect(() => {
    const saved = loadState(siteId);
    if (saved) {
      setWizardState(saved);
    } else {
      setWizardState({
        currentStep: 'booking_system',
        bookingSystem: null,
        markets: [],
        languages: [],
        contactMethods: [],
      });
    }
  }, [siteId]);

  const update = useCallback((partial: Partial<ServiceWizardState>) => {
    setWizardState(prev => ({ ...prev, ...partial }));
  }, []);

  const stepIndex = STEPS.findIndex(s => s.key === wizardState.currentStep);

  const goNext = useCallback(() => {
    const idx = STEPS.findIndex(s => s.key === wizardState.currentStep);
    if (idx < STEPS.length - 1) {
      update({ currentStep: STEPS[idx + 1].key });
    }
  }, [wizardState.currentStep, update]);

  const goBack = useCallback(() => {
    const idx = STEPS.findIndex(s => s.key === wizardState.currentStep);
    if (idx > 0) {
      update({ currentStep: STEPS[idx - 1].key });
    }
  }, [wizardState.currentStep, update]);

  // Write config to backend and trigger onboarding
  const triggerOnboarding = useCallback(async () => {
    if (!siteId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Save service config to site
      const configRes = await authFetch(`/api/sites/${siteId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_system: wizardState.bookingSystem,
          service_markets: wizardState.markets,
          service_languages: wizardState.languages,
          contact_methods: wizardState.contactMethods,
        }),
      });

      if (!configRes.ok) {
        const data = await configRes.json();
        throw new Error(data.error || 'Failed to save service configuration');
      }

      // 2. Trigger the onboarding orchestrator
      const runRes = await authFetch(`/api/aeo-wizard/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId, onboardingType: 'service' }),
      });

      if (!runRes.ok) {
        const data = await runRes.json();
        throw new Error(data.error || 'Failed to start service onboarding');
      }

      setOnboardingProgress(prev => ({ ...prev, state: 'running' }));
      startPolling();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onboarding failed');
      setOnboardingProgress(prev => ({ ...prev, state: 'failed', error: String(err) }));
    } finally {
      setIsSubmitting(false);
    }
  }, [siteId, wizardState]);

  // Poll for onboarding progress
  const pollProgress = useCallback(async () => {
    if (!siteId) return;
    try {
      const res = await authFetch(`/api/aeo-wizard/site/${siteId}`);
      if (!res.ok) return;
      const { data } = await res.json();
      if (!data) return;

      const completed = (data.steps || []).filter((s: { status: string }) => s.status === 'completed');
      const failed = (data.steps || []).find((s: { status: string }) => s.status === 'failed');

      setOnboardingProgress({
        state: data.state === 'completed' ? 'completed' : failed ? 'failed' : 'running',
        currentStep: data.currentStepLabel || data.currentStep,
        completedSteps: completed.map((s: { step_key: string }) => s.step_key),
        totalSteps: (data.steps || []).length,
        error: failed?.error_message,
      });

      if (data.state === 'completed' || data.state === 'failed' || data.state === 'cancelled') {
        if (data.state === 'completed') {
          clearState(siteId);
        }
        stopPolling();
      }
    } catch {
      // Silently retry next interval
    }
  }, [siteId]);

  const startPolling = useCallback(() => {
    pollRef.current = setInterval(pollProgress, 3000);
  }, [pollProgress]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Check if there's already a running wizard on mount
  useEffect(() => {
    (async () => {
      if (!siteId) return;
      try {
        const res = await authFetch(`/api/aeo-wizard/site/${siteId}`);
        if (!res.ok) return;
        const { data } = await res.json();
        if (data?.state === 'in_progress') {
          setOnboardingProgress(prev => ({ ...prev, state: 'running' }));
          update({ currentStep: 'run_onboarding' });
          startPolling();
        } else if (data?.state === 'completed') {
          setOnboardingProgress({
            state: 'completed',
            completedSteps: (data.steps || []).filter((s: { status: string }) => s.status === 'completed').map((s: { step_key: string }) => s.step_key),
            totalSteps: (data.steps || []).length,
          });
          update({ currentStep: 'run_onboarding' });
        }
      } catch {
        // No existing wizard, show start
      }
    })();
  }, [siteId]);

  // ── No site ──
  if (!selectedSite) {
    return (
      <div className="container mx-auto py-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site to start the onboarding wizard.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // ── Completed state ──
  if (onboardingProgress.state === 'completed') {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <h1 className="text-3xl font-bold">Service Onboarding Complete</h1>
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-6 w-6" />
              Onboarding Completed Successfully
            </CardTitle>
            <CardDescription>
              {onboardingProgress.completedSteps.length} of {onboardingProgress.totalSteps} steps completed for {selectedSite.name}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Booking System</p>
                <p className="font-medium capitalize">{wizardState.bookingSystem || 'None'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Markets</p>
                <p className="font-medium">{wizardState.markets.length} markets</p>
              </div>
              <div>
                <p className="text-muted-foreground">Languages</p>
                <p className="font-medium">{wizardState.languages.length} languages</p>
              </div>
              <div>
                <p className="text-muted-foreground">Contact Methods</p>
                <p className="font-medium">{wizardState.contactMethods.length} methods</p>
              </div>
            </div>
            <div className="pt-4 border-t">
              <Button asChild size="lg" className="w-full">
                <a href="/service-catalog">
                  <Sparkles className="mr-2 h-5 w-5" />
                  View Service Catalog
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Active wizard ──
  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Service Onboarding</h1>
        <p className="text-muted-foreground">
          {selectedSite.name} — Step {stepIndex + 1} of {STEPS.length}
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2">
        {STEPS.map((step, i) => (
          <div key={step.key} className="flex items-center gap-2 flex-1">
            <div
              className={`flex items-center justify-center h-8 w-8 rounded-full text-xs font-medium shrink-0 ${
                i < stepIndex
                  ? 'bg-green-100 text-green-700'
                  : i === stepIndex
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {i < stepIndex ? <CheckCircle className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-sm truncate ${i === stepIndex ? 'font-medium' : 'text-muted-foreground'}`}>
              {step.label}
            </span>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
          </div>
        ))}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step Content */}
      {wizardState.currentStep === 'booking_system' && (
        <Card>
          <CardHeader>
            <CardTitle>Do you use a booking system?</CardTitle>
            <CardDescription>
              We'll connect to it later to pull availability and pricing data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {BOOKING_SYSTEMS.map(({ value, label, icon: Icon }) => (
                <Card
                  key={value}
                  className={`cursor-pointer transition-all hover:border-primary ${
                    wizardState.bookingSystem === value ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''
                  }`}
                  onClick={() => update({ bookingSystem: value })}
                >
                  <CardContent className="flex items-center gap-3 p-4">
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="text-sm font-medium">{label}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex justify-end">
              <Button onClick={goNext} disabled={!wizardState.bookingSystem}>
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {wizardState.currentStep === 'markets_languages' && (
        <Card>
          <CardHeader>
            <CardTitle>Markets & Languages</CardTitle>
            <CardDescription>
              Which markets do you serve and in what languages?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">Markets</Label>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {COMMON_MARKETS.map(market => (
                  <label key={market} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={wizardState.markets.includes(market)}
                      onCheckedChange={(checked) => {
                        update({
                          markets: checked
                            ? [...wizardState.markets, market]
                            : wizardState.markets.filter(m => m !== market),
                        });
                      }}
                    />
                    {market}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Languages</Label>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {COMMON_LANGUAGES.map(lang => (
                  <label key={lang.code} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={wizardState.languages.includes(lang.code)}
                      onCheckedChange={(checked) => {
                        update({
                          languages: checked
                            ? [...wizardState.languages, lang.code]
                            : wizardState.languages.filter(l => l !== lang.code),
                        });
                      }}
                    />
                    {lang.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={goBack}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={goNext}
                disabled={wizardState.markets.length === 0 || wizardState.languages.length === 0}
              >
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {wizardState.currentStep === 'contact_methods' && (
        <Card>
          <CardHeader>
            <CardTitle>How do customers contact you?</CardTitle>
            <CardDescription>
              Select all methods your customers can use to reach you.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              {CONTACT_METHODS.map(({ value, label, icon: Icon }) => (
                <Card
                  key={value}
                  className={`cursor-pointer transition-all hover:border-primary ${
                    wizardState.contactMethods.includes(value) ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''
                  }`}
                  onClick={() => {
                    const has = wizardState.contactMethods.includes(value);
                    update({
                      contactMethods: has
                        ? wizardState.contactMethods.filter(m => m !== value)
                        : [...wizardState.contactMethods, value],
                    });
                  }}
                >
                  <CardContent className="flex items-center gap-3 p-4">
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="text-sm font-medium">{label}</span>
                    {wizardState.contactMethods.includes(value) && (
                      <CheckCircle className="h-4 w-4 ml-auto text-primary" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={goBack}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={goNext}
                disabled={wizardState.contactMethods.length === 0}
              >
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {wizardState.currentStep === 'run_onboarding' && (
        <Card>
          <CardHeader>
            <CardTitle>
              {onboardingProgress.state === 'running' ? 'Onboarding in Progress' : 'Ready to Onboard'}
            </CardTitle>
            <CardDescription>
              {onboardingProgress.state === 'running'
                ? 'The AI is analyzing your site and preparing service content.'
                : 'Review your settings and start the AI onboarding process.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm p-4 rounded-lg bg-muted/50">
              <div>
                <p className="text-muted-foreground">Booking System</p>
                <p className="font-medium capitalize">{wizardState.bookingSystem || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Markets</p>
                <p className="font-medium">{wizardState.markets.join(', ') || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Languages</p>
                <p className="font-medium">{wizardState.languages.join(', ') || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Contact</p>
                <p className="font-medium capitalize">{wizardState.contactMethods.join(', ') || '—'}</p>
              </div>
            </div>

            {/* Running state */}
            {onboardingProgress.state === 'running' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm font-medium">
                    {onboardingProgress.currentStep || 'Starting...'}
                  </span>
                </div>
                <Progress
                  value={(onboardingProgress.completedSteps.length / onboardingProgress.totalSteps) * 100}
                  className="h-2"
                />
                <p className="text-xs text-muted-foreground">
                  {onboardingProgress.completedSteps.length} of {onboardingProgress.totalSteps} steps completed
                </p>
              </div>
            )}

            {/* Failed state */}
            {onboardingProgress.state === 'failed' && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {onboardingProgress.error || 'Onboarding failed. You can retry.'}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={goBack}
                disabled={onboardingProgress.state === 'running'}
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              {onboardingProgress.state !== 'running' && (
                <Button onClick={triggerOnboarding} disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting...</>
                  ) : onboardingProgress.state === 'failed' ? (
                    <><RefreshCw className="mr-2 h-4 w-4" /> Retry Onboarding</>
                  ) : (
                    <><Play className="mr-2 h-4 w-4" /> Start Onboarding</>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
